<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue'

const props = withDefaults(defineProps<{
  text: string
  audioUrl?: string
  messageId?: string
  duration?: number
  side: 'left' | 'right'
  favorited?: boolean
  loading?: boolean
}>(), {
  duration: 3,
  favorited: false,
  loading: false,
})

const emit = defineEmits<{
  (e: 'toggleFavorite'): void
  (e: 'longPress'): void
}>()

const isPlaying = ref(false)
const progress = ref(0)
const showTranscript = ref(false)
const audioRef = ref<HTMLAudioElement | null>(null)
const resolvedAudioUrl = ref(props.audioUrl)
let animationFrame: number | null = null

// audioUrl 异步写回时（TTS 生成完成后）更新 resolvedAudioUrl
watch(() => props.audioUrl, (newUrl) => {
  if (newUrl)
    resolvedAudioUrl.value = newUrl
})

onMounted(async () => {
  if (!props.messageId)
    return

  if (props.audioUrl) {
    // 检测 blob URL 是否有效
    try {
      const res = await fetch(props.audioUrl, { method: 'HEAD' })
      if (res.ok)
        return // URL 仍有效，无需处理
    }
    catch {
      // blob URL 已失效，继续从 IndexedDB 恢复
    }
  }

  // audioUrl 缺失或已失效，从 IndexedDB 重建
  const { getVoiceAudioUrl } = await import('../../utils/voice-audio-storage')
  const newUrl = await getVoiceAudioUrl(props.messageId)
  if (newUrl)
    resolvedAudioUrl.value = newUrl
})

const estimatedDuration = computed(() => {
  if (resolvedAudioUrl.value && audioRef.value?.duration) {
    return Math.ceil(audioRef.value.duration)
  }
  return props.duration
})

const durationDisplay = computed(() => {
  const s = estimatedDuration.value
  const min = Math.floor(s / 60)
  const sec = s % 60
  return min > 0 ? `${min}:${String(sec).padStart(2, '0')}` : `0:${String(sec).padStart(2, '0')}`
})

// 生成假波形数据
const waveformBars = computed(() => {
  const count = Math.max(12, Math.min(30, Math.ceil(estimatedDuration.value * 4)))
  return Array.from({ length: count }, () => 0.2 + Math.random() * 0.8)
})

function togglePlay() {
  if (!resolvedAudioUrl.value || !audioRef.value)
    return

  if (isPlaying.value) {
    audioRef.value.pause()
    isPlaying.value = false
    if (animationFrame)
      cancelAnimationFrame(animationFrame)
  }
  else {
    audioRef.value.play()
    isPlaying.value = true
    updateProgress()
  }
}

function updateProgress() {
  if (!audioRef.value || !isPlaying.value)
    return
  progress.value = audioRef.value.currentTime / (audioRef.value.duration || 1)
  animationFrame = requestAnimationFrame(updateProgress)
}

function onAudioEnded() {
  isPlaying.value = false
  progress.value = 0
  if (animationFrame)
    cancelAnimationFrame(animationFrame)
  if (audioRef.value)
    audioRef.value.currentTime = 0
}

let longPressTimer: ReturnType<typeof setTimeout> | null = null

function onPointerDown() {
  longPressTimer = setTimeout(() => {
    // 长按统一 emit longPress，由父级决定行为（弹菜单）
    emit('longPress')
  }, 600)
}

function onPointerUp() {
  if (longPressTimer) {
    clearTimeout(longPressTimer)
    longPressTimer = null
  }
}

onBeforeUnmount(() => {
  if (animationFrame)
    cancelAnimationFrame(animationFrame)
  if (longPressTimer)
    clearTimeout(longPressTimer)
})
</script>

<template>
  <div
    class="voice-bubble"
    :class="[`voice-bubble--${side}`, { 'voice-bubble--favorited': favorited }]"
    @pointerdown="onPointerDown"
    @pointerup="onPointerUp"
    @pointerleave="onPointerUp"
  >
    <div class="voice-bubble__main" @click="resolvedAudioUrl ? togglePlay() : (showTranscript = !showTranscript)">
      <!-- 播放按钮 -->
      <div class="voice-bubble__play-btn">
        <div v-if="loading" class="voice-bubble__spinner" />
        <div v-else-if="resolvedAudioUrl" class="voice-bubble__icon" :class="isPlaying ? 'i-solar:pause-bold' : 'i-solar:play-bold'" />
        <div v-else class="voice-bubble__icon i-solar:microphone-3-bold" />
      </div>

      <!-- 波形 -->
      <div class="voice-bubble__waveform">
        <div
          v-for="(h, i) in waveformBars"
          :key="i"
          class="voice-bubble__bar"
          :class="{ 'voice-bubble__bar--active': resolvedAudioUrl ? (i / waveformBars.length) <= progress : false }"
          :style="{ height: `${h * 100}%` }"
        />
      </div>

      <!-- 时长 -->
      <span class="voice-bubble__duration">{{ durationDisplay }}</span>

      <!-- 收藏标记 -->
      <div v-if="favorited" class="voice-bubble__fav i-solar:heart-bold text-red-400" />

      <!-- 显示文本按钮（真语音条才显示） -->
      <button
        v-if="resolvedAudioUrl && text"
        class="voice-bubble__text-btn"
        :class="{ 'voice-bubble__text-btn--active': showTranscript }"
        @click.stop="showTranscript = !showTranscript"
      >
        <div class="i-solar:text-field-bold-duotone" />
      </button>
    </div>

    <!-- 转写文字 -->
    <div v-if="showTranscript && text" class="voice-bubble__transcript">
      {{ text }}
    </div>

    <audio
      v-if="resolvedAudioUrl"
      ref="audioRef"
      :src="resolvedAudioUrl"
      preload="metadata"
      @ended="onAudioEnded"
    />
  </div>
</template>

<style scoped>
.voice-bubble {
  max-width: 260px;
  user-select: none;
}

.voice-bubble__main {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 16px;
  cursor: pointer;
  transition: background-color 0.15s;
}

.voice-bubble--left .voice-bubble__main {
  background: var(--chat-bubble-other, rgba(255, 255, 255, 0.08));
}

.voice-bubble--right .voice-bubble__main {
  background: var(--chat-bubble-self, rgba(99, 102, 241, 0.2));
  flex-direction: row-reverse;
}

.voice-bubble__play-btn {
  flex-shrink: 0;
  width: 28px;
  height: 28px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.voice-bubble__icon {
  width: 18px;
  height: 18px;
  opacity: 0.8;
}

.voice-bubble__spinner {
  width: 16px;
  height: 16px;
  border: 2px solid rgba(255, 255, 255, 0.2);
  border-top-color: rgba(255, 255, 255, 0.7);
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.voice-bubble__waveform {
  display: flex;
  align-items: center;
  gap: 2px;
  height: 24px;
  flex: 1;
  min-width: 60px;
}

.voice-bubble__bar {
  width: 2px;
  min-height: 3px;
  border-radius: 1px;
  background: rgba(255, 255, 255, 0.3);
  transition: background-color 0.15s;
}

.voice-bubble__bar--active {
  background: rgba(99, 102, 241, 0.8);
}

.voice-bubble__duration {
  font-size: 12px;
  opacity: 0.6;
  flex-shrink: 0;
  font-variant-numeric: tabular-nums;
}

.voice-bubble__fav {
  width: 12px;
  height: 12px;
  flex-shrink: 0;
}

.voice-bubble__text-btn {
  flex-shrink: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: none;
  background: transparent;
  cursor: pointer;
  opacity: 0.4;
  transition: opacity 0.15s;
  padding: 0;
}

.voice-bubble__text-btn:hover,
.voice-bubble__text-btn--active {
  opacity: 0.9;
}

.voice-bubble__text-btn > div {
  width: 14px;
  height: 14px;
  color: currentColor;
}

.voice-bubble__transcript {
  padding: 4px 12px 6px;
  font-size: 12px;
  opacity: 0.6;
  line-height: 1.4;
}

.voice-bubble--right .voice-bubble__waveform {
  flex-direction: row-reverse;
}
</style>
